import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import env from '../config/env';
import { useDriverStateV2 } from './useDriverStateV2';
import { getStoredSelectedCompanyId } from './userCompanies.shared';
import { DriverPerformanceMetricsDto } from '../types';

const fetchDriverPerformance = async (
  token: string,
  companyId: string,
  driverId: string,
  startDate?: string,
  endDate?: string
): Promise<DriverPerformanceMetricsDto> => {
  try {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', startDate);
    }
    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = `${env.apiUrl}/drivers/${driverId}/performance-metrics${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-company-id': companyId,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch driver performance: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Failed to fetch driver performance:', error);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch driver performance'
    );
  }
};

export interface UseDriverPerformanceOptions {
  startDate?: string;
  endDate?: string;
}

/**
 * Hook to fetch driver performance metrics
 *
 * @param driverId - The ID of the driver to fetch performance for
 * @param options - Optional filtering parameters
 * @param options.startDate - Start date in ISO format (YYYY-MM-DD)
 * @param options.endDate - End date in ISO format (YYYY-MM-DD)
 */
export const useDriverPerformance = (
  driverId: string,
  options?: UseDriverPerformanceOptions
) => {
  const { getAccessTokenSilently } = useHybridAuth();
  // The backend checks the driver is in the caller's company, the same one
  // useDriverLeaderboard sends. An office member with no Driver row has no
  // driver company; the stored selection stands in, as in useCompanyStats.
  const { driver } = useDriverStateV2();
  const companyId = driver?.companyId || getStoredSelectedCompanyId();

  return useQuery({
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return fetchDriverPerformance(
        token,
        companyId!,
        driverId,
        options?.startDate,
        options?.endDate
      );
    },
    queryKey: [
      'driver-performance',
      companyId,
      driverId,
      options?.startDate,
      options?.endDate,
    ],
    enabled: !!driverId && !!companyId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
